import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import apiWithAuth, { setLogoutCallback } from '../services/apiWithAuth';
import { permissionsCache } from '../utils/permissionsCache';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);

  const logout = useCallback(() => {
    console.log('Logout function called');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('kd_admin_employee_profile');
    permissionsCache.clear(); // Clear cached permissions using utility
    setUser(null);
    setPermissions([]);
    setIsAuthenticated(false);
    setLoading(false);
    setIsRefreshingToken(false);
    console.log('Logout completed, isAuthenticated set to false');
  }, []);

  const setTokenRefreshLoading = useCallback((loading) => {
    console.log('AuthContext: Setting token refresh loading:', loading);
    setIsRefreshingToken(loading);
  }, []);

  const fetchUserPermissions = useCallback(async (forceRefresh = false) => {
    try {
      // Check if permissions are already cached and we're not forcing a refresh
      if (!forceRefresh) {
        const cachedPermissions = permissionsCache.get();
        if (cachedPermissions) {
          console.log('AuthContext: Using cached permissions:', cachedPermissions);
          setPermissions(cachedPermissions);
          setIsAuthenticated(true);
          setLoading(false);
          return;
        }
      }

      console.log('AuthContext: Fetching user permissions from API...');
      const response = await apiWithAuth.get('/employee-auth/permissions');
      console.log('AuthContext: Fetched permissions:', response.data);
      
      const fetchedPermissions = response.data.permissions || [];
      setPermissions(fetchedPermissions);
      
      // Cache permissions using utility
      permissionsCache.set(fetchedPermissions);
      
      // If user data is not set, try to get it from the permissions response
      if (response.data.employee) {
        console.log('AuthContext: Setting user from permissions response:', response.data.employee);
        setUser({
          id: response.data.employee._id || response.data.employee.id,
          ...response.data.employee
        });
      }
      
      setIsAuthenticated(true);
      setLoading(false);
    } catch (error) {
      console.error('AuthContext: Error fetching permissions:', error);
      // Don't call logout here - let apiWithAuth interceptor handle 401 errors
      // Only handle non-auth errors
      if (error.response?.status !== 401) {
        console.log('AuthContext: Non-401 error, setting loading to false');
        setLoading(false);
        setIsAuthenticated(false);
      }
      // For 401 errors, the apiWithAuth interceptor will handle token refresh or logout
    }
  }, []); // Remove user dependency to avoid infinite re-renders

  useEffect(() => {
    // Set the logout callback for apiWithAuth
    console.log('AuthContext: Setting logout callback for apiWithAuth');
    setLogoutCallback(logout, setTokenRefreshLoading);
  }, [logout, setTokenRefreshLoading]);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      console.log('AuthProvider mounted, token:', token);
      if (token) {
        setIsAuthenticated(true);
        // Use cached permissions on initialization, don't force refresh
        await fetchUserPermissions(false);
      } else {
        setIsAuthenticated(false);
        setLoading(false);
      }
    };
    
    initializeAuth();
  }, [fetchUserPermissions]); // Include fetchUserPermissions in dependencies

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/employee-auth/login`, {
        email,
        password
      });

      const { token, refreshToken, user, employee } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      
      // Store user data with employee ID if available
      let userData = {};
      if (employee) {
        userData = {
          id: employee._id,
          ...employee
        };
      } else if (user) {
        userData = {
          id: user._id || user.id,
          ...user
        };
      }
      
      console.log('Setting user data from login:', userData);
      setUser(userData);
      setIsAuthenticated(true);

      // Immediately fetch and cache employee profile for header/profile usage
      try {
        if (userData && userData.id) {
          const profileResponse = await apiWithAuth.get(`/employees/${userData.id}`);
          const employeeProfile = profileResponse.data.data || profileResponse.data;
          try {
            localStorage.setItem('kd_admin_employee_profile', JSON.stringify(employeeProfile));
          } catch (_) {}
        }
      } catch (e) {
        console.warn('Failed to prefetch employee profile after login:', e);
      }

      // Fetch permissions after login (force refresh to get latest permissions)
      await fetchUserPermissions(true);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed',
        status: error.response?.status
      };
    }
  };

  const refreshAuthToken = async () => {
    try {
      console.log('AuthContext: Attempting to refresh auth token...');
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/employee-auth/refresh-token`, {
        refreshToken
      });

      const { token: newToken, refreshToken: newRefreshToken } = response.data;
      localStorage.setItem('token', newToken);
      if (newRefreshToken) {
        localStorage.setItem('refreshToken', newRefreshToken);
      }

      console.log('AuthContext: Token refresh successful');
      return newToken;
    } catch (error) {
      console.error('AuthContext: Token refresh failed:', error);
      logout();
      throw error;
    }
  };

  const refreshPermissions = useCallback(async () => {
    console.log('AuthContext: Manually refreshing permissions...');
    await fetchUserPermissions(true);
  }, [fetchUserPermissions]);

  const hasPermission = (module, action) => {
    if (!permissions || permissions.length === 0) return false;

    return permissions.some(permission =>
      permission.module === module &&
      permission.actions.includes(action)
    );
  };

  const canAccessModule = (module) => {
    if (!permissions || permissions.length === 0) return false;

    return permissions.some(permission =>
      permission.module === module
    );
  };

  const value = {
    user,
    permissions,
    loading,
    isRefreshingToken,
    login,
    logout,
    refreshAuthToken,
    refreshPermissions,
    hasPermission,
    canAccessModule,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 